import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { applyTimeZone } from "../../utils/utils";
import { getEventKey } from "./calendarEventUtils";

const INITIAL_PAST_EVENTS = 1;
const INITIAL_FUTURE_EVENTS = 10;
const PAST_PAGE_SIZE = 5;
const FUTURE_PAGE_SIZE = 10;
const MAX_WINDOW_EVENTS = 40;
const SCROLL_THRESHOLD_PX = 260;
const ESTIMATED_ROW_HEIGHT = 78;

function getEventsHeight(events, start, end, rowHeights, estimatedRowHeight) {
    let height = 0;
    const safeStart = Math.max(0, Math.min(start, events.length));
    const safeEnd = Math.max(safeStart, Math.min(end, events.length));

    for (let index = safeStart; index < safeEnd; index += 1) {
        const event = events[index];

        if (!event) {
            height += estimatedRowHeight;
            continue;
        }

        const rowHeight = rowHeights.get(getEventKey(event));
        height += rowHeight || estimatedRowHeight;
    }

    return height;
}

function getInitialRange(events, nowMoment) {
    if (events.length === 0) {
        return { start: 0, end: 0 };
    }

    const firstFutureIndex = events.findIndex(event => applyTimeZone(event["data-esperada-inici"]) > nowMoment);
    const futureStart = firstFutureIndex === -1 ? events.length : firstFutureIndex;
    const start = Math.max(0, futureStart - INITIAL_PAST_EVENTS);
    const end = Math.min(events.length, futureStart + INITIAL_FUTURE_EVENTS);

    if (end > start) {
        return { start, end };
    }

    return {
        start: Math.max(0, events.length - INITIAL_PAST_EVENTS),
        end: events.length,
    };
}

function getPreviousRange(range) {
    const start = Math.max(0, range.start - PAST_PAGE_SIZE);
    const end = Math.min(range.end, start + MAX_WINDOW_EVENTS);
    return { start, end };
}

function getNextRange(range, eventsLength) {
    const end = Math.min(eventsLength, range.end + FUTURE_PAGE_SIZE);
    const start = Math.max(0, end - MAX_WINDOW_EVENTS);
    return { start, end };
}

function isSameRange(firstRange, secondRange) {
    return firstRange.start === secondRange.start && firstRange.end === secondRange.end;
}

function normalizeRangeForEvents(range, eventsLength, nowMoment, events) {
    if (eventsLength === 0) {
        return { start: 0, end: 0 };
    }

    if (range.end === 0) {
        return getInitialRange(events, nowMoment);
    }

    const end = Math.min(Math.max(range.end, 1), eventsLength);
    let start = Math.max(0, Math.min(range.start, end - 1));

    if (range.end > eventsLength || range.start >= eventsLength) {
        const filledStart = Math.max(0, end - MAX_WINDOW_EVENTS);
        start = Math.min(start, filledStart);
    }

    if (end - start > MAX_WINDOW_EVENTS) {
        start = end - MAX_WINDOW_EVENTS;
    }

    return { start, end };
}

function useCalendarEventsWindow(events, now) {
    const containerRef = useRef(null);
    const rowHeightsRef = useRef(new Map());
    const lastScrollTopRef = useRef(0);
    const hasPositionedInitialScrollRef = useRef(false);
    const nowMoment = useMemo(() => moment(now), [now]);
    const [range, setRange] = useState(() => getInitialRange(events, nowMoment));
    const visibleRange = useMemo(
        () => normalizeRangeForEvents(range, events.length, nowMoment, events),
        [range, nowMoment, events]
    );

    useEffect(() => {
        setRange(currentRange => {
            const nextRange = normalizeRangeForEvents(currentRange, events.length, nowMoment, events);
            return isSameRange(currentRange, nextRange) ? currentRange : nextRange;
        });
    }, [events, nowMoment]);

    useLayoutEffect(() => {
        if (!containerRef.current || hasPositionedInitialScrollRef.current || events.length === 0) {
            return;
        }

        const topSpacerHeight = getEventsHeight(
            events,
            0,
            visibleRange.start,
            rowHeightsRef.current,
            ESTIMATED_ROW_HEIGHT
        );

        containerRef.current.scrollTop = topSpacerHeight;
        lastScrollTopRef.current = topSpacerHeight;
        hasPositionedInitialScrollRef.current = true;
    }, [events, visibleRange.start]);

    const handleRowRef = useCallback((event, element) => {
        if (!element) {
            return;
        }

        const rowHeight = element.getBoundingClientRect().height;

        if (rowHeight > 0) {
            rowHeightsRef.current.set(getEventKey(event), rowHeight);
        }
    }, []);

    const handleScroll = useCallback(event => {
        const container = event.currentTarget;
        const scrollTop = container.scrollTop;
        const scrollingUp = scrollTop < lastScrollTopRef.current;
        const scrollingDown = scrollTop > lastScrollTopRef.current;
        lastScrollTopRef.current = scrollTop;

        setRange(currentRange => {
            const safeRange = normalizeRangeForEvents(currentRange, events.length, nowMoment, events);
            const topSpacerHeight = getEventsHeight(
                events,
                0,
                safeRange.start,
                rowHeightsRef.current,
                ESTIMATED_ROW_HEIGHT
            );
            const visibleRowsHeight = getEventsHeight(
                events,
                safeRange.start,
                safeRange.end,
                rowHeightsRef.current,
                ESTIMATED_ROW_HEIGHT
            );
            const nearTop = scrollTop <= topSpacerHeight + SCROLL_THRESHOLD_PX;
            const nearBottom = scrollTop + container.clientHeight >= topSpacerHeight + visibleRowsHeight - SCROLL_THRESHOLD_PX;

            if (nearTop && safeRange.start > 0 && scrollingUp) {
                const previousRange = getPreviousRange(safeRange);
                return isSameRange(currentRange, previousRange) ? currentRange : previousRange;
            }

            if (nearBottom && safeRange.end < events.length && scrollingDown) {
                const nextRange = getNextRange(safeRange, events.length);
                return isSameRange(currentRange, nextRange) ? currentRange : nextRange;
            }

            return isSameRange(currentRange, safeRange) ? currentRange : safeRange;
        });
    }, [events, nowMoment]);

    return {
        containerRef,
        visibleEvents: events.slice(visibleRange.start, visibleRange.end),
        topSpacerHeight: getEventsHeight(events, 0, visibleRange.start, rowHeightsRef.current, ESTIMATED_ROW_HEIGHT),
        bottomSpacerHeight: getEventsHeight(events, visibleRange.end, events.length, rowHeightsRef.current, ESTIMATED_ROW_HEIGHT),
        nowMoment,
        handleRowRef,
        handleScroll,
    };
}

export default useCalendarEventsWindow;
